import React, { useEffect, useState } from "react";
import api, { LEVEL_COLORS, BACKEND_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Trophy, Download, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import useSubscription from "../hooks/useSubscription";
import { ProLock } from "../components/ProGate";
import WeightTrackerChart from "../components/WeightTrackerChart";

export default function Progress() {
  const [tab, setTab] = useState("weight");
  const [prs, setPrs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pdfName, setPdfName] = useState("gains-progress");
  const { sub } = useSubscription();
  const isPro = sub?.is_pro;

  useEffect(() => { (async () => {
    const [p, s] = await Promise.all([
      api.get("/personal-bests"),
      api.get("/me/stats"),
    ]);
    setPrs(p.data); setStats(s.data);
  })(); }, []);

  const exportPdf = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/export/pdf?filename=${encodeURIComponent(pdfName)}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const safeName = `${pdfName || "progress"}.pdf`;
      // Try the modern File System Access API for "Save As..." dialog (Chrome/Edge)
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: safeName,
            types: [{ description: "PDF document", accept: { "application/pdf": [".pdf"] } }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success("PDF saved");
          return;
        } catch (err) {
          if (err?.name === "AbortError") { return; }
          // fall back to default download
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = safeName;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Export failed"); }
  };

  const lvlColor = stats ? LEVEL_COLORS[stats.level.name] : "#FFF";

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mb-1">Progress</h1>
      <div className="text-xs tracking-[0.2em] uppercase text-neutral-400 mb-5">Track. Compare. Celebrate.</div>

      {stats && (
        <div className="grid grid-cols-3 gap-2 mb-5 border border-neutral-800">
          <div className="p-4 border-r border-neutral-800">
            <div className="text-xs tracking-widest uppercase text-neutral-500">Level</div>
            <div className="font-display text-2xl mt-1" style={{ color: lvlColor }}>{stats.level.name}</div>
          </div>
          <div className="p-4 border-r border-neutral-800">
            <div className="text-xs tracking-widest uppercase text-neutral-500">Total Lifted</div>
            <div className="font-display text-2xl mt-1">{Math.round(stats.total_weight_kg)} kg</div>
          </div>
          <div className="p-4">
            <div className="text-xs tracking-widest uppercase text-neutral-500">PRs</div>
            <div className="font-display text-2xl mt-1">{stats.total_prs}</div>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-neutral-900 border border-neutral-800 rounded-none h-auto p-0">
          <TabsTrigger value="weight" data-testid="prog-tab-weight" className="rounded-none h-10 px-5 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">Body Weight</TabsTrigger>
          <TabsTrigger value="prs" data-testid="prog-tab-prs" className="rounded-none h-10 px-5 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">PRs</TabsTrigger>
          <TabsTrigger value="export" data-testid="prog-tab-export" className="rounded-none h-10 px-5 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">Export</TabsTrigger>
        </TabsList>

        <TabsContent value="weight" className="mt-5">
          <WeightTrackerChart />
        </TabsContent>

        <TabsContent value="prs" className="mt-5">
          {prs.length === 0 ? (
            <div className="border border-dashed border-neutral-800 p-8 text-center">
              <Trophy className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
              <div className="text-sm text-neutral-400">No PRs yet — finish a workout to earn your first.</div>
            </div>
          ) : (
            <div className="divide-y divide-neutral-800 border border-neutral-800">
              {prs.map((p) => (
                <div key={p.exercise_id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg uppercase">{p.exercise_name}</div>
                    <div className="text-xs text-neutral-500 uppercase tracking-wider">{p.best_weight} kg · {p.best_reps} reps</div>
                  </div>
                  <Trophy className="w-5 h-5 text-blue-500" />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="export" className="mt-5">
          {!isPro ? (
            <ProLock feature="PDF export" testId="pro-lock-pdf" />
          ) : (
            <div className="border border-neutral-800 p-5 space-y-4">
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">File name</Label>
                <Input data-testid="pdf-filename-input" value={pdfName} onChange={(e) => setPdfName(e.target.value)} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <Button data-testid="pdf-export-btn" onClick={exportPdf} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
              <p className="text-xs text-neutral-500">PDFs download to your browser's default downloads folder.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
